import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { SalesforceBundle } from "~/api/models/Bundle";
import { PaymentOption } from "~/api/models/PaymentOption";
import { Product } from "~/stores/checkout/product";

export interface CheckoutStoreState {
  setSelectedProducts: (selectedProducts: Product[]) => void;
  setPaymentMethod: (paymentMethod: string) => void;
  paymentMethod: string | null;
  selectedPaymentOption: PaymentOption | null;
  setSelectedPaymentOption: (option: PaymentOption | null) => void;
  selectedProducts: Product[];
  selectedBundles: SalesforceBundle[];
  hasSkinCare: boolean;
  referralCode: string | null;
  setReferralCode: (arg: string | null) => void;
  discountCode: string | null;
  setDiscountCode: (arg: string | null) => void;
  specialConditionsValues: string[] | null;
  otherSpecialConditions: string | null;
  toggleSkinCare: () => void;
  setSelectedBundles: (arg: SalesforceBundle[]) => void;
  setHasSkinCare: (arg: boolean) => void;
  setSpecialConditionsValues: (arg: string[]) => void;
  setOtherSpecialConditions: (arg: string) => void;
  contractAmount: number | null;
  setContractAmount: (amount: number | null) => void;
  nationality: string | null;
  setNationality: (nationality: string | null) => void;
  residenceStatus: string | null;
  setResidenceStatus: (status: string | null) => void;
  dateOfEntry: string | null;
  setDateOfEntry: (date: string | null) => void;
  heyLightOrderId: string | null;
  setHeyLightOrderId: (id: string | null) => void;
  individualDiscountAmount: number | null;
  setIndividualDiscountAmount: (amount: number | null) => void;
  individualDiscountPercent: number | null;
  setIndividualDiscountPercent: (percent: number | null) => void;
}

export const useCheckoutStore = create<CheckoutStoreState>()(
  persist(
    (set, get) => ({
      selectedBundles: [],
      setSelectedBundles: (bundles: SalesforceBundle[]) => {
        set({ selectedBundles: bundles });
      },
      setHasSkinCare: (arg: boolean) => {
        set({ hasSkinCare: arg });
      },
      setSelectedProducts: (selectedProducts: Product[]) => {
        set({ selectedProducts });
      },
      setPaymentMethod: (paymentMethod: string) => set({ paymentMethod }),
      toggleSkinCare: () => {
        const prev = get().hasSkinCare;
        set({
          hasSkinCare: !prev,
        });
      },
      setSpecialConditionsValues: (specialConditionsValues: string[]) => {
        set({ specialConditionsValues });
      },
      setOtherSpecialConditions: (otherSpecialConditions: string) => {
        set({ otherSpecialConditions });
      },
      setReferralCode: (referralCode: string | null) => {
        set({ referralCode: referralCode ?? null });
      },
      setDiscountCode: (discountCode: string | null) => {
        set({ discountCode: discountCode ?? null });
      },
      specialConditions: [],
      paymentMethod: null,
      selectedPaymentOption: null,
      setSelectedPaymentOption: (option: PaymentOption | null) => {
        set({
          selectedPaymentOption: option,
          paymentMethod: option?.apiName ?? null,
        });
      },
      selectedProducts: [],
      hasSkinCare: true,
      referralCode: null,
      discountCode: null,
      specialConditionsValues: null,
      otherSpecialConditions: null,
      contractAmount: null,
      setContractAmount: (amount: number | null) =>
        set({ contractAmount: amount }),
      nationality: null,
      setNationality: (nationality: string | null) => set({ nationality }),
      residenceStatus: null,
      setResidenceStatus: (status: string | null) =>
        set({ residenceStatus: status }),
      dateOfEntry: null,
      setDateOfEntry: (date: string | null) => set({ dateOfEntry: date }),
      heyLightOrderId: null,
      setHeyLightOrderId: (id: string | null) => set({ heyLightOrderId: id }),
      individualDiscountAmount: null,
      setIndividualDiscountAmount: (amount: number | null) =>
        set({ individualDiscountAmount: amount }),
      individualDiscountPercent: null,
      setIndividualDiscountPercent: (percent: number | null) =>
        set({ individualDiscountPercent: percent }),
    }),
    {
      merge: (state, currentState) => {
        // This is needed as in the persisted state, the selectedBundle is a plain object and not an instance of SalesforceBundle
        const persistedState = state as CheckoutStoreState;
        const selectedBundles = persistedState.selectedBundles;

        currentState = { ...currentState, ...persistedState };

        currentState.selectedBundles = selectedBundles.map((bundle) =>
          SalesforceBundle.fromJson(bundle)
        );

        return currentState;
      },
      storage: createJSONStorage(() => sessionStorage),
      name: "checkout",
    }
  )
);
